import { useCallback, useEffect, useState } from "react";
import { Plus, Save } from "lucide-react";
import { listTemplates, createTemplate, getLifecycleSettings, updateLifecycleSettings } from "./lifecycleApi";

const PRESET_CHAINS = {
  full: ["HR", "Department Head", "Finance", "Management"],
  manager: ["HR", "Manager"],
  hr_only: ["HR"],
};

export default function LcTemplates() {
  const [onboarding, setOnboarding] = useState([]);
  const [offboarding, setOffboarding] = useState([]);
  const [settings, setSettings] = useState({ onboarding_approval_chain: [], offboarding_approval_chain: [] });
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState({ template_type: "onboarding", name: "", description: "" });
  const [error, setError] = useState("");
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    try {
      const [ob, off, cfg] = await Promise.all([
        listTemplates({ template_type: "onboarding", page_size: 50 }),
        listTemplates({ template_type: "offboarding", page_size: 50 }),
        getLifecycleSettings(),
      ]);
      setOnboarding(ob.items || []);
      setOffboarding(off.items || []);
      setSettings(cfg);
    } catch (e) {
      setError(e?.response?.data?.detail || "Failed to load");
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const handleCreate = async (e) => {
    e.preventDefault();
    try {
      await createTemplate(form);
      setShowForm(false);
      setForm({ template_type: "onboarding", name: "", description: "" });
      load();
    } catch (err) {
      setError(err?.response?.data?.detail || "Create failed");
    }
  };

  const saveApprovalConfig = async () => {
    setSaving(true);
    try {
      await updateLifecycleSettings(settings);
      setError("");
    } catch (err) {
      setError(err?.response?.data?.detail || "Failed to save approval config");
    } finally {
      setSaving(false);
    }
  };

  const applyPreset = (key) => {
    setSettings((s) => ({ ...s, onboarding_approval_chain: [...PRESET_CHAINS[key]] }));
  };

  const TemplateList = ({ title, items }) => (
    <div className="rounded-xl border overflow-hidden" style={{ borderColor: "var(--slate-200)" }}>
      <div className="px-4 py-3 font-medium border-b" style={{ background: "var(--slate-50)" }}>{title}</div>
      <ul className="divide-y">
        {items.map((t) => (
          <li key={t.template_id} className="px-4 py-3 text-sm flex justify-between">
            <div>
              <div className="font-medium">{t.name}</div>
              <div className="text-slate-500 text-xs">{t.description || "No description"}</div>
            </div>
            <span className={`text-xs px-2 py-0.5 rounded-full ${t.is_active ? "bg-green-100 text-green-700" : "bg-slate-100"}`}>
              {t.is_active ? "Active" : "Inactive"}
            </span>
          </li>
        ))}
        {!items.length && <li className="px-4 py-6 text-center text-slate-500">No templates</li>}
      </ul>
    </div>
  );

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <h2 className="text-xl font-semibold">Templates &amp; Workflow Config</h2>
        <button type="button" onClick={() => setShowForm(true)} className="lc-btn-primary">
          <Plus className="w-4 h-4" /> New Template
        </button>
      </div>

      {error && <p className="text-red-600 text-sm">{error}</p>}

      <div className="rounded-xl border p-4 space-y-4" style={{ background: "var(--bg-surface, #fff)" }}>
        <h3 className="font-medium">Configurable Approval Workflow</h3>
        <p className="text-sm text-slate-500">Define onboarding approval levels. New onboardings use this chain.</p>
        <div className="flex flex-wrap gap-2">
          <button type="button" onClick={() => applyPreset("full")} className="px-3 py-1 text-xs border rounded-lg">HR → Dept Head → Finance → Mgmt</button>
          <button type="button" onClick={() => applyPreset("manager")} className="px-3 py-1 text-xs border rounded-lg">HR → Manager</button>
          <button type="button" onClick={() => applyPreset("hr_only")} className="px-3 py-1 text-xs border rounded-lg">HR Only</button>
        </div>
        <label className="block text-sm">
          Onboarding approval chain (comma-separated)
          <input
            className="mt-1 w-full border rounded-lg px-3 py-2"
            value={(settings.onboarding_approval_chain || []).join(", ")}
            onChange={(e) => setSettings({
              ...settings,
              onboarding_approval_chain: e.target.value.split(",").map((s) => s.trim()).filter(Boolean),
            })}
          />
        </label>
        <button type="button" onClick={saveApprovalConfig} disabled={saving} className="inline-flex items-center gap-1 px-4 py-2 bg-violet-600 text-white rounded-lg text-sm">
          <Save className="w-4 h-4" /> {saving ? "Saving…" : "Save Approval Config"}
        </button>
      </div>

      {showForm && (
        <form onSubmit={handleCreate} className="rounded-xl border p-4 space-y-3" style={{ background: "var(--bg-surface, #fff)" }}>
          <select className="border rounded-lg px-3 py-2 text-sm" value={form.template_type} onChange={(e) => setForm({ ...form, template_type: e.target.value })}>
            <option value="onboarding">Onboarding Template</option>
            <option value="offboarding">Offboarding Template</option>
          </select>
          <input required className="w-full border rounded-lg px-3 py-2 text-sm" placeholder="Template name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} />
          <textarea className="w-full border rounded-lg px-3 py-2 text-sm" placeholder="Description" value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} />
          <div className="flex gap-2">
            <button type="submit" className="px-4 py-2 bg-violet-600 text-white rounded-lg text-sm">Save</button>
            <button type="button" onClick={() => setShowForm(false)} className="px-4 py-2 border rounded-lg text-sm">Cancel</button>
          </div>
        </form>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <TemplateList title="Onboarding Templates" items={onboarding} />
        <TemplateList title="Offboarding Templates" items={offboarding} />
      </div>
    </div>
  );
}
